<template>
  <section
    class="section carousel1"
    v-editable="blok"
    ref="refRoot"
    :class="{ hasMargin: blok.hasMargin }"
  >
    <div class="bg-top" />

    <div
      class="carousel-wrapper"
      ref="refCarouselWrapper"
    >
      <div
        class="bg-black"
        :style="{
          '--gradientTopFrom': `80%`,
          '--gradientTopTo': '100%',

          '--gradientBottomDirection': 'to top',
          '--gradientBottomFrom': `0%`,
          '--gradientBottomTo': '20%',
        }"
      />

      <ul
        ref="refCarousel"
        class="main-carousel"
      >
        <li
          ref="refCarouselSlides"
          v-for="(item, idx) in blok.items"
          :key="item._uid"
          class="carousel-cell"
        >
          <component
            class="item"
            ref="refCarouselItems"
            :key="item._uid"
            :is="item.component"
            :blok="item"
            :idx="idx"
            :isFirst="idx === 0"
            :isLast="idx === blok.items.length - 1"
          />
        </li>
      </ul>
    </div>

    <div class="bottom">
      <div class="bg-bottom" />
      <div class="the-container">
        <CarouselPrevNextControls
          v-if="showPrevNextControls"
          :length="blok.items.length"
          :idx="slideIdx"
          :reach="reach"
          @prev="onPrev"
          @next="onNext"
        />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { useRaf } from "@resn/gozer-vue"
import "flickity/css/flickity.css"

const { $gsap, $ScrollTrigger } = useNuxtApp()

const props = defineProps({
  blok: {
    type: Object,
    required: true,
  },
  blokPrev: {
    type: Object,
  },
  blokNext: {
    type: Object,
  },
})
const emits = defineEmits(["loaded"])

const story = inject("story")
const { app, debug, storyblok } = useStores()
const env = useEnv()
const showPrevNextControls = computed(() => !env.isMobile || !app.modalStoryId)

const refRoot = ref(null)
const refCarousel = ref(null)
const refCarouselSlides = ref([])
const refCarouselItems = ref([])

// ************************************************************
// Carousel
// ************************************************************
const reach = ref("start")

const gap = useCssVar(refRoot, "--grid-column-gap")
const px = usePx(gap)
const spaceBetween = computed(() => {
  return env.isMobile ? px.value * 0.5 : px.value
})

const slideIdx = ref(0)
const onSlideChange = (idx) => {
  // slideIdx.value = swiper.activeIndex
  slideIdx.value = idx
}

watchEffect(() => {
  if (slideIdx.value >= props.blok.items.length - 1) {
    reach.value = "end"
  } else if (slideIdx.value <= 0) {
    reach.value = "start"
  } else {
    reach.value = null
  }
})

const onSlideClick = (evt, pointer, ele, idx) => {
  refCarouselItems.value[idx]?.onClick?.()
}

const onPrev = () => {
  // refSwiper.value.slidePrev()
  if (!flkty.value) return
  if (reach.value === "start") {
    return
  }
  flkty.value.previous()
}
const onNext = () => {
  // refSwiper.value.slideNext()
  if (!flkty.value) return
  if (reach.value === "end") {
    return
  }
  flkty.value.next()
}

const bus = useBus()
const onScroll = (evt, progress) => {
  bus.emit("carousel-update", progress)

  const x = flkty.value.x

  if (-flkty.value.slidesWidth > x) {
    reach.value = "end"
  } else if (x > -0.01) {
    reach.value = "start"
  } else {
    reach.value = null
  }
  // console.log("onScroll", flkty.value.x, reach.value)
}

const flkty = ref(null)
provide("flkty", flkty)
onMounted(async () => {
  const Flickity = (await import("flickity")).default
  flkty.value = new Flickity(refCarousel.value, {
    cellAlign: "left",
    contain: true,
    // autoPlay: 2000,
    pauseAutoPlayOnHover: true,
    prevNextButtons: false,
    pageDots: false,

    on: {
      ready: function () {
        // console.log("Flickity ready")
        // emits("loaded")
      },
    },
  })
  flkty.value.on("change", onSlideChange)
  flkty.value.on("staticClick", onSlideClick)
  flkty.value.on("scroll", onScroll)
  // emits("loaded")
  // console.log(100, flkty.value)
})

// useRaf(() => {
//   if (!flkty.value) return
//   console.log(100, flkty.value.x)
// })

useDotsTrigger({
  trigger: refRoot,
  to: { ambientStrength: 0, flowMapStrength: 0 },
})

// ************************************************************
// ScrollTrigger - animate in
// ************************************************************
// const refCarouselWrapper = ref(null)
// const scroller = inject("scroller")
// useScrollTrigger(() => {
//   let tl = $gsap.timeline({
//     scrollTrigger: {
//       // scroller: refRoot.value.closest(".smooth-wrapper"),
//       trigger: refCarouselWrapper.value,
//       start: "top bottom",
//       toggleActions: "play none none reset",
//       // markers: true,
//     },
//   })
//   tl.add("start")

//   let slides = refCarouselWrapper.value.querySelectorAll(".carousel-cell")
//   for (let idx = 0; idx < slides.length; idx++) {
//     let slide = slides[idx]
//     let stagger = idx * 0.1

//     tl.from(
//       slide,
//       {
//         duration: 0.7,
//         x: () => `${window.innerWidth * 0.15}`,
//         // rotationY: "-15deg",
//       },
//       `start+=${stagger}`
//     )
//   }

//   return tl
// })
</script>

<style lang="scss" scoped>
@import "@/styles/shared.scss";

.carousel1 {
  position: relative;
  padding-top: 0;
}

.bg-top {
  position: relative;
  width: 100%;
  height: $p10;
  background: black;
}

.carousel-wrapper {
  position: relative;
}

.main-carousel {
  width: 100%;
  height: 49rem;
  padding-inline: calc((100% - var(--grid-width)) / 2);
  overflow: visible;
  // @include flex(row, flex-start);
}

.carousel-cell {
  width: fit-content;
}

.carousel1.hasMargin {
  .carousel-cell {
    margin-right: var(--grid-column-gap);

    &:last-child {
      margin-right: 0;
    }
  }
}

:deep(.flickity-viewport) {
  overflow: visible;
}
:deep(.flickity-slider) {
}

.bottom {
  position: relative;
  padding-top: $p6;

  .bg-bottom {
    @include fill;
    background: black;
    z-index: -1;
  }

  // .the-container {
  //   z-index: 2;
  //   pointer-events: auto;
  // }
}

.carousel-prev-next-controls {
  @include flex(row, flex-end);
  // margin-top: $p6;
}

@include mobile {
  .main-carousel {
    height: 37.4rem;
  }
}
</style>
